import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, OnDestroy, signal, viewChild, ViewChild, AfterViewInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { FwAvatarModule, FwButtonModule, FwContainedInputModule, FwIconModule, FwLayoutsModule, FwMenuModule, FwSelectMenuModule, FwTextInputModule, FwProgressModule, FwAvatarListComponent, FwTooltipDirective, FwDialogService } from '@flywheel-io/vision';
import { debounceTime, distinctUntilChanged, map, Subscription } from 'rxjs';

import { DirectivesModule } from '../../common/directives.module';
import { ConfigService } from '../../config.service';
import { CreateProjectDialog } from '../create-project/create-project-dialog.component';
import { MaterialModule } from '../../material.module';
import { ProjectLockStatusComponent } from '../project-lock-status/project-lock-status.component';
import { SessionModule } from '../../sessions/session.module';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { Project, ProjectWithStats } from '../../shared/models/project.model';
import { PipesModule } from '../../shared/pipes.module';
import { SharedModule } from '../../shared/shared.module';
import { ViewerService } from '../../viewers/viewer.service';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { ProjectsQueryService } from '../projects-query.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { ProfileService } from '../../profile/profile.service';
import { FeatureFlag } from '../../shared/models/feature-flags.model';
import { PermissionIfDirective } from '../../shared/directives/permission-if.directive';
import { FeatureIfDirective } from '../../shared/directives/feature-if.directive';
import { CdkMenuTrigger } from '@angular/cdk/menu';
import { LdapIndicatorComponent } from '../ldap-indicator/ldap-indicator.component';

interface ActionMenuItem {
  label: string,
  href: string,
  permissions?: `${PermissionAction}`[],
  features?: FeatureFlag[],
}

@Component({
  host: {
    'test-id': 'project-list',
  },
  selector: 'project-list',
  templateUrl: './project-list.component.html',
  styleUrls: ['./project-list.component.scss'],
  imports: [
    CdkMenuTrigger,
    CommonModule,
    DirectivesModule,
    FeatureIfDirective,
    FwAvatarModule,
    FwAvatarListComponent,
    FwButtonModule,
    FwContainedInputModule,
    FwIconModule,
    FwLayoutsModule,
    FwMenuModule,
    FwSelectMenuModule,
    FwTextInputModule,
    FwTooltipDirective,
    MaterialModule,
    PipesModule,
    ReactiveFormsModule,
    SessionModule,
    SharedModule,
    RouterModule,
    PermissionIfDirective,
    ProjectLockStatusComponent,
    FwProgressModule,
    LdapIndicatorComponent,
    FeatureIfDirective,
],
})
export class ProjectListComponent implements OnDestroy, AfterViewInit {
  private config = inject(ConfigService);
  private dialog = inject(FwDialogService);
  private viewerService = inject(ViewerService);

  readonly dataSource = new MatTableDataSource<ProjectWithStats>()
  error: Error = null
  readonly features = {
    auditTrail: this.config.isFeatureEnabled('audit_trail'),
    deidProfile: this.config.isFeatureEnabled('deid_profile'),
    jupyterIntegrationEnabled: this.config.isFeatureEnabled('jupyter_integration'),
    ldapSync: this.config.isFeatureEnabled('ldap_sync'),
    ohifReact: this.config.isFeatureEnabled('ohif_react'),
    projectExport: this.config.isFeatureEnabled('project_export'),
    projectImport: this.config.isFeatureEnabled('project_import'),
    projectLocking: this.config.isFeatureEnabled('project_locking'),
    readerTasks: this.config.isFeatureEnabled('reader_tasks'),
  }

  private projectQueryService = inject(ProjectsQueryService);
  projectQuery = injectQuery(() => this.projectQueryService.paginatedProjects(this.page, this.parsedSort, this.parsedFilters));
  projectRolesQuery = injectQuery(() => this.projectQueryService.allProjectRoles());

  page = signal(1);
  readonly pageSize = 15
  readonly projectColumns = ['lock', 'label', 'group', 'subject_count', 'session_count', 'roles', 'users', 'actions']

  usersQuery = injectQuery(() => this.projectQueryService.allUsers());
  userOptions = computed(() => {
    return this.usersQuery.data()?.results || [];
  })

  @ViewChild(MatPaginator) paginator: MatPaginator;

  /***** FILTERS *****/
  readonly filters = new FormGroup({
    project: new FormControl(''),
    group: new FormControl(''),
    roles: new FormControl([]),
    users: new FormControl([]),
  })

  filterValueSignal = toSignal(this.filters.valueChanges.pipe(debounceTime(100)));

  parsedFilters = computed(() => {
    const values = this.filterValueSignal();
    let filterString = '';

    filterString += values?.project ? `label=~(?i)${values.project},` : '';
    filterString += values?.group ? `group.label=~(?i)${values.group},` : ''
    filterString += values?.users?.length > 0 ? `permissions._id|=[${values.users.toString()}],` : '';
    filterString += values?.roles?.length > 0 ? `permissions|={_id=${this.profile()._id},role_ids=|[${values.roles.toString()}]}` : '';

    return filterString;
  })

  // used for determining what to render when the list is empty
  filtersActive = computed(() => {
    const filters = this.filterValueSignal() || {};
    const filterKeys = Object.keys(filters);
    const activeFilters = filterKeys.filter(key => filters[key]);
    return activeFilters.length > 0;
  })

  /***** SORTING ******/
  matSort = viewChild.required(MatSort);
  parsedSort = signal('label:asc');

  ngAfterViewInit(): void {

    const columnLabelToApiNameMap = {
      label: 'label',
      group: 'group.label',
      subject_count: 'stats.number_of.subjects',
      session_count: 'stats.number_of.sessions',
    }

    // it's awkward to get a signal of a viewChild's observable
    // so just using a subscription to update a signal
    this.subscriptions.sort = this.matSort().sortChange.pipe(
      map(sortChange => {
        const parsedColumnName = columnLabelToApiNameMap[sortChange.active];

        const parsed = `${parsedColumnName}:${sortChange.direction || 'asc'}`
        return parsed;
      }),
      distinctUntilChanged(),
    ).subscribe(parsedSort => {
      // reset pagination when sort changes
      this.page.set(1);
      this.parsedSort.set(parsedSort)
    });
  }

  private subscriptions = {
    sort: Subscription.EMPTY,
  }

  private profileService = inject(ProfileService);
  profile = toSignal(this.profileService.profile$);

  setTableData = effect(() => {
    this.dataSource.data = this.projectQuery.data()?.results as unknown as ProjectWithStats[];
  })

  constructor() {

    if (!this.features.projectLocking) {
      this.projectColumns = this.projectColumns.filter(key => key !== 'lock');
    }
  }

  onPageChange(pageEvent: PageEvent): void {
    // the paginator is 0-based, the API expects 1-based page numbers
    this.page.set(pageEvent.pageIndex + 1);
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  getProjectId(index: number, project: Project): string {
    return project._id;
  }

  getFlaggedTooltip(project: ProjectWithStats): string {
    const flags = project.noncompliant_session_count;
    if (flags > 0) {
      return flags > 1
        ? `${flags} flagged sessions`
        : `${flags} flagged session`;
    }
    return '';
  }

  getUsersRoleLabel(project: Project): string {
    const roles = this.projectRolesQuery.data()?.results;
    const user = this.profile();

    if (!roles || !user) {
      return '';
    }

    const userPerms = project.permissions.find(perm => perm._id === user._id);

    if (!userPerms) {
      return '';
    }

    const roleLabels = userPerms.role_ids.map(roleId => {
      return roles.find(role => role._id === roleId)?.label;
    })

    return roleLabels.join(', ') || '';
  }

  newProject(): void {
    // creating a new project will refresh projects
    this.dialog.open(CreateProjectDialog);
  }

  viewProjectStudies(project: Project): void {
    this.viewerService.ohifViewerDialog({ projectId: project._id });
  }

  actionMenuItems: ActionMenuItem[] = [
    { label: 'Description', href: 'description' },
    { label: 'Sessions', href: 'sessions' },
    { label: 'Subjects', href: 'subjects' },
    { label: 'Information', href: 'info' },
    { label: 'Analyses', href: 'analyses', permissions: ['analyses_view_metadata'] },
    { label: 'Data Views', href: 'dataviews', permissions: ['data_views_view'] },
    { label: 'Permissions', href: 'permissions', permissions: [PermissionAction.ProjectPermissionsView] },
    { label: 'Templates', href: 'templates', permissions: ['session_templates_view'], features: [] },
    { label: 'Rules', href: 'gear-rules', permissions: ['gear_rules_view'], features: [] },
    { label: 'Tasks', href: 'tasks', features: ['reader_tasks'] },
    { label: 'Imports', href: 'imports', permissions: ['imports_manage', 'files_create_bulk'], features: ['project_import'] },
    { label: 'Exports', href: 'exports', permissions: ['exports_manage', 'files_download'], features: ['project_import'] },
    { label: 'Task Reports Queue', href: 'task-report-queue', permissions: ['tasks_assign', 'tasks_create', 'tasks_modify', 'tasks_view'], features: ['tasks_refactor'] },
    { label: 'Applications', href: 'applications', permissions: ['settings_view'] },
    { label: 'Settings', href: 'settings', permissions: ['settings_view'] },
    { label: 'Workspaces', href: 'workspaces', features: ['jupyter_integration'] },
    { label: 'Audit Trail', href: 'auditTrailReports', permissions: ['audit_trail_reports_view', 'audit_trail_reports_manage'], features: ['audit_trail'] },
  ];
}
